import { css } from '@emotion/css';

export const SourceSansPro: string = 'Source Sans Pro';

const FONT_SIZE_BASE_UNIT = 1;
const LINE_HEIGHT_BASE_UNIT = 1;

/** Defined on `:root` in `index.css` (16px, matching Dealmaker and the iOS text floor). */
export const MOBILE_MIN_FONT_SIZE = 'var(--mobile-min-font-size)';
/** Defined on `:root` in `index.css` (24px = 1.5 × 16px). */
export const MOBILE_MIN_LINE_HEIGHT = 'var(--mobile-min-line-height)';

/**
 * Viewport where `index.css` sets the root font to 12px.
 * Body `rem` type would otherwise shrink below the 16px floor.
 */
const MOBILE_TYPE_MAX_WIDTH = '768px';

/** Caption stays below body text. 0.75rem is 9px when the root is 12px. */
const MOBILE_CAPTION_FONT_SIZE = '14px';
const MOBILE_CAPTION_LINE_HEIGHT = '20px';
/** Chart text stays below captions. 0.6875rem is about 8px when the root is 12px. */
const MOBILE_XXS_FONT_SIZE = '12px';
const MOBILE_XXS_LINE_HEIGHT = '16px';

type MobileFloor = {
  fontSize: string;
  lineHeight: string;
};

const fontStyle = (fontSize: number, lineHeight: number, mobileFloor?: MobileFloor) => {
  const fontSizeValue = `${FONT_SIZE_BASE_UNIT * fontSize}rem`;
  const lineHeightValue = `${LINE_HEIGHT_BASE_UNIT * lineHeight}rem`;
  const floorFontSize = mobileFloor?.fontSize ?? MOBILE_MIN_FONT_SIZE;
  const floorLineHeight = mobileFloor?.lineHeight ?? MOBILE_MIN_LINE_HEIGHT;

  return css`
    font-size: ${fontSizeValue} !important;
    line-height: ${lineHeightValue} !important;
    @media (max-width: ${MOBILE_TYPE_MAX_WIDTH}) {
      font-size: max(${floorFontSize}, ${fontSizeValue}) !important;
      line-height: max(${floorLineHeight}, ${lineHeightValue}) !important;
    }
  `;
};

export const FontWeight = {
  light: css({
    fontWeight: 300,
  }),
  normal: css({
    fontWeight: 400,
  }),
  semibold: css({
    fontWeight: 600,
  }),
  bold: css({
    fontWeight: 700,
  }),
};

export const FontFamily = {
  sourceSansPro: css({
    fontFamily: 'Source Sans Pro, sans-serif !important',
  }),
};

/** Rem multipliers. 1rem is 16px on desktop and 12px on mobile (`index.css`). */
const FontRem = {
  h1: { fontSize: 3, lineHeight: 4.2 },
  h2: { fontSize: 2.625, lineHeight: 3.687 },
  h3: { fontSize: 2.25, lineHeight: 3.125 },
  h4: { fontSize: 2, lineHeight: 2.812 },
  h5: { fontSize: 1.75, lineHeight: 2.437 },
  h6: { fontSize: 1.5, lineHeight: 2.125 },
  lg: { fontSize: 1.125, lineHeight: 1.562 },
  md: { fontSize: 1, lineHeight: 1.375 },
  sm: { fontSize: 0.875, lineHeight: 1.25 },
  xs: { fontSize: 0.75, lineHeight: 1.0625 },
  xxs: { fontSize: 0.6875, lineHeight: 0.935 },
} as const;

export const Font = {
  h1: fontStyle(FontRem.h1.fontSize, FontRem.h1.lineHeight),
  h2: fontStyle(FontRem.h2.fontSize, FontRem.h2.lineHeight),
  h3: fontStyle(FontRem.h3.fontSize, FontRem.h3.lineHeight),
  h4: fontStyle(FontRem.h4.fontSize, FontRem.h4.lineHeight),
  h5: fontStyle(FontRem.h5.fontSize, FontRem.h5.lineHeight),
  h6: fontStyle(FontRem.h6.fontSize, FontRem.h6.lineHeight),
  lg: fontStyle(FontRem.lg.fontSize, FontRem.lg.lineHeight),
  md: fontStyle(FontRem.md.fontSize, FontRem.md.lineHeight),
  sm: fontStyle(FontRem.sm.fontSize, FontRem.sm.lineHeight),
  xs: fontStyle(FontRem.xs.fontSize, FontRem.xs.lineHeight, {
    fontSize: MOBILE_CAPTION_FONT_SIZE,
    lineHeight: MOBILE_CAPTION_LINE_HEIGHT,
  }),
  xxs: fontStyle(FontRem.xxs.fontSize, FontRem.xxs.lineHeight, {
    fontSize: MOBILE_XXS_FONT_SIZE,
    lineHeight: MOBILE_XXS_LINE_HEIGHT,
  }),
};

export const TextAlign = {
  center: css({
    textAlign: 'center',
  }),
  justify: css({
    textAlign: 'justify',
  }),
  left: css({
    textAlign: 'left',
  }),
  right: css({
    textAlign: 'right',
  }),
};
